// client/src/App.jsx
import React, { useState, useEffect, useCallback } from "react";
import OrdersPage from "./pages/OrdersPage";
import CommandPage from "./pages/CommandPage";
import WatchdogPage from "./pages/WatchdogPage";
import VerifyLocationPage from "./pages/VerifyLocationPage";
import LoginPage from "./pages/LoginPage";
import { checkShopifyConnection, fetchCommandConfig } from "./services/api";
import NotificationCenter from "./components/NotificationCenter";
import apiClient from "./services/api";
import { API_BASE_URL } from "./config/apiBase";
import { setAppTimezone } from "./utils/timezone";

const TOKEN_KEY = "shipguard_token";
const USER_KEY = "shipguard_user";

const navStyle = {
  nav: {
    background: "#0f172a",
    padding: "0 32px",
    display: "flex",
    alignItems: "center",
    height: 56,
    flexShrink: 0,
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    gap: 0,
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginRight: 32,
  },
  logoIcon: {
    width: 30, height: 30, borderRadius: 8,
    background: "linear-gradient(135deg,#8b5cf6,#6366f1)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 14,
  },
  logoText: {
    fontWeight: 900, fontSize: 15, color: "white", letterSpacing: "-0.02em",
  },
  tab: (active) => ({
    padding: "16px 20px",
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    color: active ? "#8b5cf6" : "#94a3b8",
    cursor: "pointer",
    background: "transparent",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    borderBottomWidth: 2,
    borderBottomStyle: "solid",
    borderBottomColor: active ? "#8b5cf6" : "transparent",
    transition: "all 0.2s",
    height: 56,
    display: "flex",
    alignItems: "center",
  }),
  storeInfo: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "#94a3b8",
  },
  dot: (ok) => ({
    width: 7, height: 7, borderRadius: "50%",
    background: ok ? "#22c55e" : "#f59e0b",
    boxShadow: ok ? "0 0 6px #22c55e" : "none",
  }),
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: 8,
    transition: "background 0.15s",
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid rgba(139,92,246,0.3)",
  },
  avatarPlaceholder: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#8b5cf6,#6366f1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    color: "white",
  },
};

const App = () => {
  const [page, setPage] = useState("monitor");
  const [store, setStore] = useState(null);
  const [shopifyOk, setShopifyOk] = useState(false);

  // Auth state
  const [authConfig, setAuthConfig] = useState(null); // { authEnabled, authMethod }
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  // Fetch auth config on mount
  useEffect(() => {
    fetch(`${API_BASE_URL}/auth/config`)
      .then((r) => r.json())
      .then((data) => {
        setAuthConfig(data);
        // If auth is not enabled, auto-login
        if (!data.authEnabled) {
          setUser({ email: "dev@localhost", name: "Developer", picture: null });
          setToken("dev");
        }
        setAuthLoading(false);
      })
      .catch(() => {
        // If auth endpoint fails, assume no auth
        setAuthConfig({ authEnabled: false, authMethod: null });
        setUser({ email: "dev@localhost", name: "Developer", picture: null });
        setToken("dev");
        setAuthLoading(false);
      });
  }, []);

  // Set auth header on API client when token changes
  useEffect(() => {
    if (token && token !== "dev") {
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, [token]);

  // Check Shopify connection and load config (timezone etc.) after auth
  useEffect(() => {
    if (!user) return;
    checkShopifyConnection()
      .then((data) => { setStore(data.store); setShopifyOk(true); })
      .catch(() => { setShopifyOk(false); });
    fetchCommandConfig()
      .then((res) => {
        const tz = res?.data?.display_timezone;
        if (tz) setAppTimezone(tz);
      })
      .catch(() => {});
  }, [user]);

  const handleLogin = useCallback((newToken, newUser) => {
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
    }
    if (newUser) {
      localStorage.setItem(USER_KEY, JSON.stringify(newUser));
      setUser(newUser);
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    delete apiClient.defaults.headers.common["Authorization"];
  }, []);

  // Show loading spinner during auth check
  if (authLoading) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0f172a",
        color: "#94a3b8",
        fontSize: 14,
      }}>
        Loading...
      </div>
    );
  }

  // Show login page if auth is enabled and user is not logged in
  if (authConfig?.authEnabled && !user) {
    return (
      <LoginPage
        onLogin={handleLogin}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: (page === "command" || page === "watchdog") ? "#0f1219" : "#f0f4f8", display: "flex", flexDirection: "column" }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', 'DM Sans', system-ui, sans-serif; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${(page === "command" || page === "watchdog") ? "#0d1117" : "#f1f5f9"}; }
        ::-webkit-scrollbar-thumb { background: ${(page === "command" || page === "watchdog") ? "#1e293b" : "#cbd5e1"}; border-radius: 3px; }
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
      `}</style>

      {/* Navigation Bar */}
      <nav style={navStyle.nav}>
        <div style={navStyle.logo}>
          <div style={navStyle.logoIcon}>{"\uD83D\uDEE1\uFE0F"}</div>
          <span style={navStyle.logoText}>ShipGuard</span>
        </div>

        <button style={navStyle.tab(page === "monitor")} onClick={() => setPage("monitor")}>
          {"\uD83D\uDCCA"} Monitor
        </button>
        <button style={navStyle.tab(page === "command")} onClick={() => setPage("command")}>
          {"\u26A1"} Command
        </button>
        <button style={navStyle.tab(page === "watchdog")} onClick={() => setPage("watchdog")}>
          {"\uD83D\uDC15"} Watchdog
        </button>
        <button style={navStyle.tab(page === "verify")} onClick={() => setPage("verify")}>
          {"\uD83D\uDCCD"} Verify
        </button>

        <div style={navStyle.storeInfo}>
          <NotificationCenter />
          <div style={{ width: 1, height: 24, background: "rgba(255,255,255,0.1)", flexShrink: 0 }} />
          <span style={navStyle.dot(shopifyOk)} />
          {store ? (
            <span>{store.name} · {store.myshopify_domain}</span>
          ) : (
            <span>{shopifyOk ? "Connected" : "Shopify not connected"}</span>
          )}

          {/* User avatar + logout */}
          {user && authConfig?.authEnabled && (
            <>
              <div style={{ width: 1, height: 24, background: "rgba(255,255,255,0.1)", flexShrink: 0 }} />
              <div style={navStyle.userInfo} onClick={handleLogout} title={`${user.name} (${user.email}) — Click to sign out`}>
                {user.picture ? (
                  <img src={user.picture} alt="" style={navStyle.avatar} referrerPolicy="no-referrer" />
                ) : (
                  <div style={navStyle.avatarPlaceholder}>
                    {(user.name || user.email || "?")[0].toUpperCase()}
                  </div>
                )}
                <span style={{ fontSize: 12, color: "#e2e8f0", fontWeight: 600 }}>
                  {user.name || user.email}
                </span>
              </div>
            </>
          )}
        </div>
      </nav>

      {/* Page Content */}
      {page === "monitor" && (
        <main style={{ flex: 1, padding: "28px 32px", maxWidth: 1400, margin: "0 auto", width: "100%" }}>
          <div style={{ marginBottom: 24 }}>
            <h1 style={{
              fontSize: 26, fontWeight: 900,
              color: "#0f172a", letterSpacing: "-0.03em", margin: "0 0 4px",
            }}>
              Order Monitor
            </h1>
            <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>
              Real-time Shopify order tracking · Two-Layer Protection
            </p>
          </div>
          <OrdersPage />
        </main>
      )}
      {page === "command" && <CommandPage />}
      {page === "watchdog" && <WatchdogPage />}
      {page === "verify" && <VerifyLocationPage />}
    </div>
  );
};

export default App;
